import { useEffect, useState, useMemo, type FC } from 'react';
import { enqueueSnackbar } from 'notistack';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import PrintRounded from '@mui/icons-material/PrintRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { createFilterOptions } from '@mui/material/Autocomplete';

// common
import SchemaForm from 'common/SchemaForm';
import { LINKING_STATUS } from 'common/constants';
import { HookPaginationProps } from 'common/hook/usePaginationHook';
import { apiClient } from 'api/apiClient';

// screen
import { SupplierFilterModel } from 'modules/suppliers/types';
import useGetListClients from 'modules/account/services/useGetListClients';
import { Client } from 'modules/account/types';

interface FilterProps {
  hookPagination: HookPaginationProps<SupplierFilterModel>;
  selectedClient: Client | null;
  onSetCustomer: (client: Client | null) => void;
}

const FilterBox: FC<FilterProps> = (props) => {
  const { hookPagination, selectedClient, onSetCustomer } = props;
  const [filterdClients, setFilteredClients] = useState<Client[]>([]);

  const filterClientOptions = useMemo(
    () =>
      createFilterOptions<Client>({
        stringify: (option) => `${option?.name || ''} ${option?.operationCode || ''}`.trim(),
      }),
    []
  );

  const { setParams, clearParams, filters } = hookPagination;

  // HOTFIX: xem chú thích ở SupplierListPage — tạm đổi type 2 -> 1 để dropdown khách hàng không rỗng.
  const { data } = useGetListClients({ type: 1 });

  useEffect(() => {
    if (!selectedClient && !data) {
      setFilteredClients([]);
      return;
    }

    if (!data) {
      setFilteredClients([]);
      return;
    }

    setFilteredClients(data);
  }, [data, selectedClient]);

  const schemaConfig = useMemo(
    () => ({
      fields: ({
        formProps: {
          intl: { formatMessage },
          isMobile,
        },
        methods: { setValue },
      }) => {
        return {
          customerOperationCode: {
            mode: 'auto-complete' as const,
            propsWrapper: { tablet: 4 },
            label: formatMessage({ id: 'supplier.customer' }),
            placeholder: formatMessage({ id: 'supplier.customer' }),
            rules: { required: true },
            options: filterdClients,
            rawOptions: true,
            disableClearBtn: true,
            onChange: (client) => {
              onSetCustomer(client || null);
            },
            filterOptions: filterClientOptions,
            getOptionLabel: (option: any) =>
              (typeof option === 'string'
                ? option
                : option?.name || option?.operationCode || '') as any,
            isOptionEqualToValue: (option: any, value: any) =>
              // compare by operationCode when available, fall back to id
              option?.operationCode && value?.operationCode
                ? option.operationCode === value.operationCode
                : option?.id === value?.id,
          },
          status: {
            mode: 'select' as const,
            label: formatMessage({ id: 'supplier.supplierStatus' }),
            options: LINKING_STATUS,
            propsWrapper: { tablet: 4 },
            disableClearBtn: true,
            rules: { required: true },
          },
          submit: {
            mode: 'button' as const,
            type: 'submit' as const,
            variant: 'contained' as const,
            startIcon: <SearchRounded />,
            children: !isMobile && formatMessage({ id: 'search' }),
            propsWrapper: {
              mobile: isMobile ? 2 : undefined,
              height: isMobile ? undefined : '40px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              marginTop: isMobile ? '0px' : '18px',
              marginBottom: '0px',
            },
          },
          actions: {
            mode: 'node' as const,
            propsWrapper: {
              mobile: 12,
              tablet: 'auto',
              marginTop: isMobile ? '0px' : '18px',
            },
            render: () => (
              <Box sx={{ display: 'flex', gap: 1, height: '40px', alignItems: 'stretch' }}>
                <Button
                  variant="outlined"
                  sx={{ minWidth: 40, px: 1, flexShrink: 0 }}
                  onClick={() => {
                    clearParams();
                    setValue('customerOperationCode', null as any);
                    setValue('status', 'enabled' as any);
                    onSetCustomer(null);
                  }}
                >
                  <RefreshRounded />
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PrintRounded />}
                  disabled={!selectedClient?.operationCode}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={async () => {
                    const customerOperationCode = selectedClient?.operationCode;
                    if (!customerOperationCode) return;
                    try {
                      const url = await apiClient().SupplierProfileController_getA3Preview({
                        params: { customerOperationCode },
                      });
                      if (url) window.open(url, '_blank', 'noopener,noreferrer');
                    } catch {
                      enqueueSnackbar({
                        message: 'Không lấy được URL A3 Preview',
                        variant: 'error',
                      });
                    }
                  }}
                >
                  {formatMessage({ id: 'supplier.a3Preview' })}
                </Button>
              </Box>
            ),
          },
        };
      },
    }),
    [filterdClients, clearParams, onSetCustomer, filterClientOptions, selectedClient]
  );

  const formDataValue = useMemo(() => {
    // Find the Client object that matches the stored operationCode
    const clientFromFilters = filters.customerOperationCode
      ? filterdClients.find((c) => c.operationCode === filters.customerOperationCode)
      : null;

    return {
      customerOperationCode: (selectedClient || clientFromFilters || null) as any,
      status: filters.status || 'available',
    };
  }, [filters.customerOperationCode, filters.status, selectedClient, filterdClients]);

  return (
    <SchemaForm<SupplierFilterModel>
      onSubmit={(val) => {
        setParams({
          customerOperationCode: val.customerOperationCode.operationCode,
          status: val.status,
        });
      }}
      formData={formDataValue}
      schema={schemaConfig}
    />
  );
};

export default FilterBox;
